/**
 * one-stop/actions — the CTA layer, behind one interface.
 *
 * WHAT A CTA IS HERE
 * The prototype's buttons imply four actions, and all four are declared,
 * authorization-ruled PUBLIC OPERATIONS on the backend side — no verb endpoint
 * is minted per button:
 *
 *   Join the line   →  joinPublicQueue → position / leave
 *   Book (a time)   →  createHold → checkout          (for THAT slot)
 *   Register        →  submitPublicEventRegistration
 *   Find my visit   →  guest lookup                   (still no operation)
 *
 * WHY AN INTERFACE AND NOT DIRECT CLIENT CALLS
 * The block components are written against `OneStopActions` and nothing else, so
 * the live implementation lands HERE and not a line of any block component
 * changes. That seam is what let milestone 2 ship every CTA disabled and this
 * drop turn them on in one file.
 *
 * `livePortalActions()` is that implementation (operations kit
 * `wiring-kits/one-stop-actions/`, JAI-1155). `unavailableActions(reason)` is
 * still here and still the DEFAULT with no provider: the Journey Builder preview
 * must render every CTA without being able to join a real queue or take a real
 * booking on an admin's behalf.
 *
 * THE NAMESPACED CALLS, NOT THE TOP-LEVEL ONES (kit §2.0)
 * `queue.*` and `journey.*` are the contract-typed functions. The same names at
 * the barrel's top level are older hand-typed duplicates missing fields the
 * contract declares (top-level `checkout` has no `answers`, no `attendees`, no
 * `group_name`; `searchSlots` throws the per-day `date` away). Importing the
 * namespaces is not a style choice.
 *
 * THE TOKEN RULE
 * A queue join returns an `entryId` that is both the primary key AND the
 * credential for reading the position and for leaving. It lives in the CTA
 * component's own React state for the lifetime of that view and is passed back
 * on the position and leave calls. It is NEVER put in a URL — not a query
 * string, not a hash, not a share link — never in storage, and never in a
 * module-level singleton. A URL carrying it lands in the referrer header, in
 * history and in a screenshot, and anyone holding it can remove that person from
 * the queue. The `ticket` and `checkInCode` are the human-facing identifiers.
 *
 * IDEMPOTENCY, STATED RATHER THAN ASSUMED (kit §6)
 * There is no `Idempotency-Key` on these operations yet (JAI-1160). So: every
 * submit control disables on first click and stays disabled until the call
 * settles; nothing here auto-retries a POST; and the queue join form collects a
 * phone, because a phone is the only thing that makes THAT call idempotent.
 * `checkout` has no protection but the disabled button — that is the honest
 * state, and it is why `holdExpired`/`HOLD_INVALID` copy says "we may already
 * have your booking" rather than inviting a second submission.
 *
 * CC 0031 (JAI-1160) describes an automatic per-call key plus an optional
 * per-intent key (`IdempotentCallOptions`, `newIdempotencyKey()`) on the four
 * public writes. NEITHER IS IN THIS KIT'S CLIENT. `newIdempotencyKey()` exists
 * only in `api-client/src/billing.ts`, travels in the request BODY of the
 * payments family, and `client.ts` states in terms that mutations are a separate
 * lane; `IdempotentCallOptions` does not appear anywhere in the package. So no
 * intent key is threaded here — passing a second argument the wrapper does not
 * accept would not compile, and minting one that is never sent would be theatre.
 * The paragraph above therefore still describes this build exactly. Pending on
 * the client landing the option; the call sites that would take it are
 * `joinPublicQueue`, `createHold`, `checkout` and
 * `submitPublicEventRegistration`, all in `livePortalActions()` below.
 */
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { queueRefusalText } from "../../lib/queue-refusal";
import type { JSX, ReactNode } from "react";
import { isApiError, journey, queue, submitPublicEventRegistration } from "@v0y4ge/api-client";
import type { PortalOpening, PortalZonedTime } from "@v0y4ge/api-contracts";
import type { StringKey } from "./strings";

/* ── what a visitor has to tell us ───────────────────────────────────────── */

/**
 * The one details shape all three flows need, because all three REQUIRE it:
 * `joinPublicQueue` requires `name`, `checkout` requires the customer's first
 * name, last name and email, and `submitPublicEventRegistration` requires the
 * same three. Nothing optional is collected for its own sake — `phone` is here
 * because it is what makes a queue join idempotent (kit §2.1b).
 */
export interface VisitorDetails {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  /** Empty string when not supplied. Omitted from the wire when empty. */
  readonly phone: string;
}

/** The four fields, in the order they are rendered — and therefore focused. */
export type VisitorFieldName = keyof VisitorDetails;

export const VISITOR_FIELDS: readonly VisitorFieldName[] = [
  "firstName",
  "lastName",
  "email",
  "phone",
];

/**
 * What is wrong with each field, as CATALOG KEYS rather than sentences: the
 * rules are the same in every locale, the words are not, and the form resolves
 * them against the document's language at render (JAI-1154's rule).
 */
export type VisitorProblems = Readonly<Partial<Record<VisitorFieldName, StringKey>>>;

/**
 * A DELIBERATELY LOOSE address check, and the looseness is the point.
 *
 * The authority on whether an address exists is the mail that does or does not
 * arrive; the authority on whether it is acceptable is the server. This catches
 * only what is certainly not an address — `pat`, `pat@`, `pat@example` — so the
 * form stops sending requests the server will certainly refuse, without
 * inventing a second, stricter, invisible rule that rejects addresses the
 * platform would have accepted (a client-side RFC 5322 attempt is how a valid
 * `firstname+tag@sub.domain.travel` gets told it is wrong by the wrong party).
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u;

/**
 * Digits, spaces, `+`, `-`, `.` and parentheses, and nothing else. The contract
 * accepts ANY human format, so this is a typo guard, not a format: it refuses
 * `abc` and accepts `+41 44 555 66 77`, `(415) 555-0123` and `0044-20-7946-0018`
 * alike. No normalisation happens here — what the visitor typed is what is sent.
 */
const PHONE_SHAPE = /^[\d\s+().-]+$/u;

/** Below this, whatever was typed is not a reachable number in any plan. */
const MIN_PHONE_DIGITS = 7;

/**
 * The client-side rules, per field.
 *
 * `requireEmail` is not a preference, it is which OPERATION is behind the form:
 * `QueueJoinRequest.email` is optional (valid address or `null`), while
 * `checkout` and the public event registration both require it. One form
 * component, two requirements — so the caller says which, and the form never
 * guesses.
 */
export function validateVisitor(
  v: VisitorDetails,
  options?: { readonly requireEmail?: boolean },
): VisitorProblems {
  const problems: Partial<Record<VisitorFieldName, StringKey>> = {};

  if (v.firstName.trim() === "") problems.firstName = "visitor.error.firstName";
  if (v.lastName.trim() === "") problems.lastName = "visitor.error.lastName";

  const email = v.email.trim();
  if (email === "") {
    if (options?.requireEmail !== false) problems.email = "visitor.error.emailRequired";
  } else if (!EMAIL_SHAPE.test(email)) {
    problems.email = "visitor.error.email";
  }

  const phone = v.phone.trim();
  if (phone !== "") {
    const digits = phone.replace(/\D/gu, "").length;
    if (!PHONE_SHAPE.test(phone) || digits < MIN_PHONE_DIGITS) {
      problems.phone = "visitor.error.phone";
    }
  }

  return problems;
}

/**
 * Whether a details set can be sent to the operations that require all three
 * fields. Field-level marking is the form's — this is the coarse answer, kept
 * because it is part of this package's public surface.
 */
export function visitorIsComplete(v: VisitorDetails): boolean {
  return Object.keys(validateVisitor(v, { requireEmail: true })).length === 0;
}

const displayName = (v: VisitorDetails): string =>
  `${v.firstName.trim()} ${v.lastName.trim()}`.trim();

/* ── what a refusal said, and about which field ─────────────────────────── */

/** Field-level messages, keyed by the SERVER's own field (or group) name. */
export type WireFieldMessages = Readonly<Record<string, string>>;

/**
 * One refusal, split by what its messages can actually name.
 *
 * `fields` are messages that came keyed — by a field, or by a GROUP (an object
 * in the payload rather than an input). `form` are messages that came keyed by
 * nothing at all (Zod's `formErrors`) and belong to the form as a whole.
 */
export interface RefusalMessages {
  readonly fields: WireFieldMessages;
  readonly form: readonly string[];
}

const NO_MESSAGES: RefusalMessages = { fields: {}, form: [] };

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The first non-empty string in a `string[]` value, or `undefined`. */
function firstMessage(value: unknown): string | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.find((entry): entry is string => typeof entry === "string" && entry !== "");
}

/** Every non-empty string in a `string[]` value. */
function allMessages(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string" && entry !== "");
}

/**
 * ONE NORMALISER, because the wire has FOUR shapes and the form wants one.
 *
 * `err.fields` — the `{path, code, message}` array — is the platform's standard
 * channel and the only one where `path` binds to an input unambiguously, so it
 * WINS whenever it is populated. The backend is converging every public
 * operation onto it (JAI-1171); when that lands, everything below this line
 * stops being reached and can be deleted in one commit.
 *
 * Until then it is empty (`source: 'partial'`) on all three public forms, and
 * the three of them answer the SAME `VALIDATION_ERROR` with three different
 * `details` — wire facts, probed on the live tenant, not expectations:
 *
 *   queue join           `details.email: ["Invalid email"]`
 *   event registration   `details.fieldErrors.email: ["Invalid email"]`   (Zod flatten)
 *   checkout             `details.customer: ["Invalid email"]`            (keyed by the OBJECT)
 *
 * A reader that knows only the first shape fixes one form of three and leaves
 * the other two showing the generic line — passing its own acceptance test while
 * two thirds of the defect survives, which is the failure mode worth naming.
 *
 * ⛔ ONLY STRING-ARRAY VALUES ARE READ OUT OF `details`. When the envelope
 * carries no `details` at all, the client puts the whole error blob there
 * instead; a looser reader would then present `code` and `message` as if they
 * were field errors — the exact "Invalid request body" under the Email label
 * this change exists to remove.
 *
 * `isApiError`, never `instanceof` — the client is bundled into several contexts
 * and `instanceof` answers `false` across a duplicated module instance.
 */
export function refusalMessages(err: unknown): RefusalMessages {
  if (!isApiError(err)) return NO_MESSAGES;

  // 1. The declared channel. A populated `fields` needs no interpretation.
  const fromFields: Record<string, string> = {};
  for (const field of err.fields) {
    if (!field.path || !field.message) continue;
    if (fromFields[field.path] === undefined) fromFields[field.path] = field.message;
  }
  if (Object.keys(fromFields).length > 0) return { fields: fromFields, form: [] };

  // 2. The three `details` shapes.
  const details: unknown = err.details;
  if (!isPlainRecord(details)) return NO_MESSAGES;

  // Zod's `flatten()` nests the per-field map one level in and puts the
  // unkeyed messages beside it. Both halves are read; neither is invented.
  const nested = isPlainRecord(details["fieldErrors"]) ? details["fieldErrors"] : null;
  const map = nested ?? details;
  const form = allMessages(details["formErrors"]);

  const fields: Record<string, string> = {};
  for (const [key, value] of Object.entries(map)) {
    if (key === "formErrors" || key === "fieldErrors") continue;
    const message = firstMessage(value);
    if (message !== undefined && fields[key] === undefined) fields[key] = message;
  }

  return { fields, form };
}

/**
 * The server's field names, mapped onto the form's.
 *
 * `name` is ONE field on the wire — `QueueJoinRequest.name` is the whole display
 * name, which the form builds as first + last — and TWO inputs on screen, so its
 * message lands under BOTH. The `first_name` / `last_name` pair is `checkout`'s
 * customer shape; `firstName` / `lastName` is the event registration's. Every
 * spelling a live operation can answer with is here, because a message with
 * nowhere to land is a message the visitor never sees.
 */
const WIRE_TO_VISITOR: Readonly<Record<string, readonly VisitorFieldName[]>> = {
  name: ["firstName", "lastName"],
  first_name: ["firstName"],
  firstname: ["firstName"],
  last_name: ["lastName"],
  lastname: ["lastName"],
  email: ["email"],
  phone: ["phone"],
  mobile: ["phone"],
};

/**
 * Payload OBJECTS that wrap the visitor's own inputs. `checkout` refuses with
 * `details.customer`, which names the group and not the input inside it.
 *
 * ⚠️ A GROUP MESSAGE IS SHOWN TWICE, ON PURPOSE, AND IT IS A COMPROMISE. It goes
 * on the FORM, where it is unambiguously true, and it is echoed under Email,
 * which is where a group-keyed message lands correctly in the case that produced
 * it (`"Invalid email"`). The echo is a guess about which input is meant and it
 * will be wrong the day the same envelope refuses `first_name` instead — which
 * is why the form-level line is the one that always renders and never depends on
 * the guess. It exists so a checkout refusal is never invisible while the wire
 * is still keying by object; it retires with JAI-1171.
 */
const WIRE_GROUPS: ReadonlySet<string> = new Set(["customer", "attendee", "guest", "registration"]);

/** Where a group-keyed message is echoed. See `WIRE_GROUPS`. */
const GROUP_ECHO: readonly VisitorFieldName[] = ["email"];

/**
 * The LEAF of a field path, lowercased: `customer.email` and `body.customer.email`
 * both name the same input, and the envelope is not consistent about the prefix.
 */
function wireFieldKey(path: string): string {
  const segments = path.split(".");
  return (segments[segments.length - 1] ?? path).toLowerCase();
}

/**
 * The subset of a refusal's field map that this form can actually show, resolved
 * onto the form's own field names. Anything that maps nowhere is dropped here
 * rather than rendered raw — an unmapped key means the message is about
 * something the visitor cannot see or fix, and the form-level line is then the
 * honest answer.
 */
export function visitorFieldMessages(
  fields: WireFieldMessages,
): Readonly<Partial<Record<VisitorFieldName, string>>> {
  const out: Partial<Record<VisitorFieldName, string>> = {};
  for (const [path, message] of Object.entries(fields)) {
    const key = wireFieldKey(path);
    const targets = WIRE_TO_VISITOR[key] ?? (WIRE_GROUPS.has(key) ? GROUP_ECHO : undefined);
    if (!targets) continue;
    for (const field of targets) {
      if (out[field] === undefined) out[field] = message;
    }
  }
  return out;
}

/**
 * What belongs on the form rather than on one input: the unkeyed `formErrors`,
 * plus every message keyed by a GROUP, which names no input by itself.
 */
export function visitorFormMessages(refusal: RefusalMessages): readonly string[] {
  const out: string[] = [...refusal.form];
  for (const [path, message] of Object.entries(refusal.fields)) {
    if (!WIRE_GROUPS.has(wireFieldKey(path))) continue;
    if (!out.includes(message)) out.push(message);
  }
  return out;
}

/* ── the operations, as this renderer needs them ─────────────────────────── */

/** The public entry-status vocabulary, verbatim — not remapped into a shorter one. */
export type QueueEntryState = "waiting" | "called" | "serving" | "done" | "left" | "no-show";

export interface QueueTicket {
  /** What the student is told to look for — the ticket the desk calls. */
  readonly reference: string;
  /** Quoted at the desk. `null` on a recovered entry that predates check-in codes. */
  readonly checkInCode: string | null;
  /** Caller-held credential. In memory only. */
  readonly positionToken: string;
  readonly peopleAhead: number | null;
  readonly estimatedWaitMinutes: number | null;
  readonly state: QueueEntryState;
  /**
   * `true` when the join matched an existing active entry and returned THAT one
   * (HTTP 200, not 201). A state, not a footnote: the screen says "you're
   * already in this line", not a second confirmation.
   */
  readonly recovered: boolean;
}

export interface QueuePosition {
  readonly reference: string;
  readonly peopleAhead: number | null;
  readonly estimatedWaitMinutes: number | null;
  readonly state: QueueEntryState;
}

/**
 * One bookable slot, carrying the wire's OWN values untouched. `startUtc` /
 * `endUtc` are true UTC instants and go back into `createHold` verbatim — no
 * reformatting, no re-zoning, no rounding (kit §2.5.1). Formatting happens at
 * render, never here.
 */
export interface AvailabilitySlot {
  readonly startUtc: string;
  readonly endUtc: string;
  readonly staffId: string | null;
  readonly staffName: string | null;
  readonly resourceId: string | null;
  readonly durationMinutes: number;
}

export interface AppointmentHold {
  readonly holdId: string;
  /** UTC instant the hold lapses. A real deadline — render the countdown. */
  readonly expiresAt: string | null;
  readonly startUtc: string;
  readonly endUtc: string;
}

export interface BookingConfirmation {
  readonly reference: string;
  /**
   * The visitor's own manage page for THIS booking, or `null`.
   *
   * `manage_url` on the checkout response's booking item (JAI-1188): a URL whose
   * PATH is a signed token, per booking. It is optional and ABSENT — not empty —
   * on a deployment with no manage surface provisioned (JAI-1189, which is every
   * tenant today), so `null` means "there is no self-service link", never "the
   * link failed to load".
   *
   * ⛔ THE TOKEN IS A BEARER CREDENTIAL. It lives here, in render state, for as
   * long as the confirmation is on screen, and nowhere else: never logged, never
   * in an analytics payload, never in a `data-*` attribute, never in storage,
   * never in the page URL, and never rendered as text. The reference is a label
   * and is safe in a photograph of the screen; this is not.
   *
   * NOT DERIVED, EVER. Nothing in the URL comes from `id` or `booking_ref`, so
   * there is no fallback that constructs one — the field is bound or the
   * reference stays plain text.
   */
  readonly manageUrl: string | null;
}

export interface EventRegistrationRef {
  /** `orderRef` — the DISPLAY reference. Put this on the screen. */
  readonly reference: string;
  /**
   * `data.registration.id`, the guest id — the HANDLE the read and cancel routes
   * take. Held, never displayed. `null` when the response omitted it (a screen
   * that stored `orderRef` instead would fail for the first time on a real
   * tenant; kit §2.3b).
   */
  readonly handle: string | null;
  /* Already-registered (JAI-2279, absorbed 2026-10-05): "already" = the server answered alreadyRegistered; reference is "" and handle null, and the block renders a neutral state with neither. */
  readonly outcome: "registered" | "waitlist" | "already";
}

/**
 * The four public flows, and nothing else. Deliberately narrow: this is the
 * renderer's view of the operations, not a re-declaration of the API. Anything
 * that needs more than this needs a contract conversation, not a wider
 * interface.
 */
export interface OneStopActions {
  /** False in the Journey Builder preview, or wherever nothing may be called. */
  readonly available: boolean;
  /** Why, in the page's language. Rendered next to the disabled control. */
  readonly unavailableReason: string | null;
  /**
   * TRUE ONLY FOR `simulatedPortalActions()`. Nothing reaches a server, nothing
   * is recorded, and every reference on screen is invented — so the HOST that
   * installed it owes the operator a visible label saying so (the OneStop tab
   * draws one over the frame). Optional so an implementation that predates this
   * flag reads as what it is: not a simulation.
   *
   * Blocks may branch on it ONLY to stand in for data a live page would have
   * been served and a preview cannot be (an appointment fragment's openings).
   * It is never a reason to change what a flow ASKS for: the same required
   * fields, the same validation, the same refusals, or the walkthrough stops
   * being a walkthrough of the real thing.
   */
  readonly simulated?: boolean;

  /**
   * HAND THE VISITOR TO THE JOURNEY THAT OWNS THIS FLOW, carrying what they
   * tapped. Present only when the host installed one; a block that finds it
   * prefers it over its own inline flow.
   *
   * WHY A ONESTOP PAGE HANDS OFF AT ALL. The three journeys already exist as
   * published pages with their own slugs, their own availability queries, their
   * own payment, question and consent steps, and their own published copy. This
   * page is the front door: it says what is bookable, what the wait is and what
   * is on — and then it should send the visitor into the flow that was built for
   * the job rather than keep a second, thinner copy of it inside a card. The
   * inline flows below stay in the file for the case with no journey page to
   * open (a standalone embed), which is why this is optional rather than a
   * replacement.
   *
   * THE PAYLOAD IS THE POINT. Every target carries the id the visitor chose, so
   * the flow opens ON that service, desk or event instead of at its own first
   * step. Tapping a named service and landing on a service picker is the whole
   * reason a front door leaks.
   */
  readonly handoff?: (target: OneStopHandoffTarget) => void;

  joinQueue(input: {
    readonly queueId: string;
    readonly visitor: VisitorDetails;
    /** The visitor ticked the data-protection consent (CC consent packet, 2026-10-17). Only `true` can be passed. */
    readonly consent: true;
  }): Promise<QueueTicket>;
  queuePosition(input: {
    readonly queueId: string;
    readonly positionToken: string;
  }): Promise<QueuePosition>;
  leaveQueue(input: { readonly queueId: string; readonly positionToken: string }): Promise<void>;

  /** The card path: the fragment's own opening, resolved to instants at use. */
  holdOpening(input: {
    readonly serviceId: string;
    readonly opening: PortalOpening;
  }): Promise<AppointmentHold>;
  /** The picker path: a searched slot, passed through verbatim. */
  holdSlot(input: {
    readonly serviceId: string;
    readonly slot: AvailabilitySlot;
  }): Promise<AppointmentHold>;
  /** What is actually free on one day — the answer to a 409, and to "another time". */
  openingsOn(input: {
    readonly serviceId: string;
    readonly date: string;
  }): Promise<readonly AvailabilitySlot[]>;
  /** On abandon, on back, on a slot change. Not politeness — correctness. */
  releaseHold(input: { readonly holdId: string }): Promise<void>;
  checkoutHold(input: {
    readonly holdId: string;
    readonly visitor: VisitorDetails;
    /** The visitor ticked the data-protection consent (CC consent packet, 2026-10-17). Only `true` can be passed. */
    readonly consent: true;
  }): Promise<BookingConfirmation>;

  registerForEvent(input: {
    readonly eventId: string;
    readonly visitor: VisitorDetails;
    readonly waitlist: boolean;
    /** The visitor ticked the data-protection consent (CC 0434). Only `true` can be passed. */
    readonly consent: true;
  }): Promise<EventRegistrationRef>;

  lookupVisits(input: { readonly phone: string }): Promise<void>;
}

/** Thrown by every method of an unavailable implementation. Carries the reason. */
export class OneStopActionsUnavailable extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "OneStopActionsUnavailable";
  }
}

/* ── civil time → instant, in the OPENING's own zone ─────────────────────── */

/**
 * A fragment opening is a CIVIL wall-clock time plus its IANA zone; `createHold`
 * wants ISO UTC instants. The two are one deterministic resolution apart, and
 * this is that resolution — done in the OPENING's zone, never the viewer's, and
 * never via a bare `new Date(civilString)`, which reads it as local time.
 *
 * A slot landing on a DST transition is the one genuinely ambiguous case: this
 * takes the EARLIER instant and lets the server's `SLOT_UNAVAILABLE` correct it.
 * That is deliberate — inventing a rule here would put a second, invisible
 * authority on the client.
 */
export function zonedCivilToInstant(when: PortalZonedTime): string {
  const time = when.time.length === 5 ? `${when.time}:00` : when.time;
  const civilAsUtc = Date.parse(`${when.date}T${time}Z`);
  if (Number.isNaN(civilAsUtc)) throw new Error("");
  // Two passes: the first offset is read at the naive instant, the second at the
  // corrected one. Converges for every zone whose offset is stable across the
  // correction, which is every zone outside the transition hour itself.
  let instant = civilAsUtc;
  for (let pass = 0; pass < 2; pass += 1) {
    instant = civilAsUtc - zoneOffsetMs(when.timeZone, new Date(instant));
  }
  return new Date(instant).toISOString();
}

function zoneOffsetMs(timeZone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(at);
  const read = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const local = Date.UTC(
    read("year"),
    read("month") - 1,
    read("day"),
    read("hour") % 24,
    read("minute"),
    read("second"),
  );
  return local - at.getTime();
}

/* ── the live implementation ─────────────────────────────────────────────── */

const slotOf = (dto: {
  start_utc: string;
  end_utc: string;
  staff_id: string | null;
  staff_name: string | null;
  resource_id: string | null;
  duration_minutes: number;
}): AvailabilitySlot => ({
  startUtc: dto.start_utc,
  endUtc: dto.end_utc,
  staffId: dto.staff_id,
  staffName: dto.staff_name,
  resourceId: dto.resource_id,
  durationMinutes: dto.duration_minutes,
});

const stringOr = (value: unknown, fallback: string | null): string | null =>
  typeof value === "string" && value ? value : fallback;

/**
 * The booking item's `manage_url`, read DEFENSIVELY off the response.
 *
 * The field is declared on the backend contract (JAI-1188) but this kit's copy of
 * `CheckoutBookingItem` predates it, so the typed shape does not carry it. The
 * client does not `parse()` the checkout response — it hands the body through —
 * so the key DOES arrive at run time; reading it as `unknown` binds the live
 * field without widening a contract this drop has no mandate to touch. It
 * disappears from here the day the contracts package is regenerated.
 *
 * ⛔ ONLY AN ABSOLUTE `https:` URL ON THE TENANT'S OWN MANAGE ORIGIN IS
 * ACCEPTED. Anything else — a relative path, a `javascript:` string, a
 * non-string, an https URL pointing anywhere else — answers `null` and the
 * reference stays plain text. An href is the one place a hostile value in this
 * field would become executable, and "the tenant sent something we do not
 * recognise" is not a reason to put it in one.
 *
 * THE ALLOWLIST IS THE DELIVERED DOCUMENT (0037/JAI-1229):
 * `<meta name="manage-base-url" content="https://manage.DOMAIN">`, stamped per
 * request host by the portal's asset worker — the same channel, and the same
 * shape, as `identity-base-url` in the typed client's `META_IDENTITY_BASE`. The
 * portal ships ONE tenant-agnostic bundle, so a build-time value cannot serve
 * this check; the document is the only per-tenant part of the response.
 *
 * ⛔ ORIGIN, NOT PREFIX. `startsWith(base)` would admit
 * `https://manage.tenant.example.evil.test/...`. The comparison is
 * `new URL(raw).origin`, in a try/catch, because a malformed string must answer
 * `null` rather than throw.
 *
 * ⛔ AN ABSENT OR EMPTY META MEANS "NO ALLOWLIST" → PLAIN TEXT. It does NOT fall
 * back to the old any-https behaviour: the worker injects nothing when it cannot
 * resolve an origin it trusts, and in that state showing the reference is
 * better than offering a link nobody vouched for. Local/dev documents that carry
 * the meta prove the linked branch (0039); ones that do not, correctly do not.
 */
const MANAGE_BASE_META_NAME = "manage-base-url";

/** Read LAZILY on every check, never memoized — a memo would freeze whatever the
 *  first call happened to see, which in a test is usually "nothing yet". */
function manageBaseOrigin(): string {
  if (typeof document === "undefined" || typeof document.querySelector !== "function") return "";
  return (
    document
      .querySelector(`meta[name="${MANAGE_BASE_META_NAME}"]`)
      ?.getAttribute("content")
      ?.trim() ?? ""
  );
}

function manageUrlOf(booking: unknown): string | null {
  if (typeof booking !== "object" || booking === null) return null;
  const raw = (booking as Record<string, unknown>)["manage_url"];
  if (typeof raw !== "string" || raw === "") return null;
  if (!/^https:\/\/[^\s]+$/iu.test(raw)) return null;
  const allowed = manageBaseOrigin();
  if (!allowed) return null;
  try {
    return new URL(raw).origin === allowed ? raw : null;
  } catch {
    return null;
  }
}

/**
 * The CTA operations, live. Every id it sends came off a fragment or off
 * `block.config` at run time — there is no seed, mock or fictional id anywhere
 * in this file, and no route string: only the typed client.
 */
export function livePortalActions(): OneStopActions {
  return {
    available: true,
    unavailableReason: null,
    simulated: false,

    async joinQueue({ queueId, visitor }) {
      const entry = await queue.joinPublicQueue(queueId, {
        name: displayName(visitor),
        // Optional on the wire — and the ONLY thing that dedupes a double tap.
        ...(visitor.phone.trim() ? { phone: visitor.phone.trim() } : {}),
        // OMITTED when empty, never sent as `""`: `email` is optional here, but an
        // empty string is not an address and fails the contract's own check.
        ...(visitor.email.trim() ? { email: visitor.email.trim() } : {}),
        agreedToTerms: true,
      });
      return {
        reference: entry.ticket,
        checkInCode: entry.checkInCode,
        positionToken: entry.entryId,
        // `position` is the 1-based place in line; people ahead is one fewer.
        // That relation is the kit's own (§7: "the join lands at position N+1
        // where the card said N ahead"), not a guess — and the position read
        // below reports `peopleAhead` directly, so this is only the first frame.
        peopleAhead: Math.max(0, entry.position - 1),
        estimatedWaitMinutes: entry.estWaitMins,
        state: entry.status,
        recovered: entry.recoveredExisting,
      };
    },

    async queuePosition({ queueId, positionToken }) {
      const pos = await queue.getPublicQueuePosition(queueId, positionToken);
      return {
        reference: pos.ticket,
        peopleAhead: pos.peopleAhead,
        estimatedWaitMinutes: pos.estWaitMins,
        state: pos.status,
      };
    },

    async leaveQueue({ queueId, positionToken }) {
      await queue.leavePublicQueue(queueId, positionToken);
    },

    async holdOpening({ serviceId, opening }) {
      const startUtc = zonedCivilToInstant(opening.start);
      const endUtc = new Date(
        Date.parse(startUtc) + opening.durationMinutes * 60_000,
      ).toISOString();
      const held = await journey.createHold({
        service_id: serviceId,
        slot_start_utc: startUtc,
        slot_end_utc: endUtc,
        ...(opening.staffId ? { staff_id: opening.staffId } : {}),
      });
      return {
        holdId: held.id,
        expiresAt: held.expires_at,
        startUtc: held.slot_start_utc,
        endUtc: held.slot_end_utc,
      };
    },

    async holdSlot({ serviceId, slot }) {
      // VERBATIM: the searched slot's own instants and ids, unchanged.
      const held = await journey.createHold({
        service_id: serviceId,
        slot_start_utc: slot.startUtc,
        slot_end_utc: slot.endUtc,
        ...(slot.staffId ? { staff_id: slot.staffId } : {}),
        ...(slot.resourceId ? { resource_id: slot.resourceId } : {}),
      });
      return {
        holdId: held.id,
        expiresAt: held.expires_at,
        startUtc: held.slot_start_utc,
        endUtc: held.slot_end_utc,
      };
    },

    async openingsOn({ serviceId, date }) {
      const avail = await journey.searchAvailability({
        service_id: serviceId,
        date_from: date,
        date_to: date,
      });
      // `date` is a VENUE-local calendar day and is matched as the string it is.
      const day = avail.dates.find((d) => d.date === date) ?? avail.dates[0];
      return (day?.slots ?? []).map(slotOf);
    },

    async releaseHold({ holdId }) {
      await journey.releaseHold(holdId);
    },

    async checkoutHold({ holdId, visitor }) {
      const order = await journey.checkout({
        hold_ids: [holdId],
        customer: {
          first_name: visitor.firstName.trim(),
          last_name: visitor.lastName.trim(),
          email: visitor.email.trim(),
          ...(visitor.phone.trim() ? { phone: visitor.phone.trim() } : {}),
          // ⚠️ `notification_preferences` is OMITTED. The contract and the
          // worker disagree on its shape (JAI-1163) and it is the one field
          // that can fail a checkout that would otherwise succeed.
        },
        agreedToTerms: true,
      });
      const booking = order.bookings[0];
      return {
        reference: booking?.booking_ref ?? booking?.ref ?? "",
        manageUrl: manageUrlOf(booking),
      };
    },

    async registerForEvent({ eventId, visitor, waitlist }) {
      const result = await submitPublicEventRegistration({
        eventId,
        firstName: visitor.firstName.trim(),
        lastName: visitor.lastName.trim(),
        email: visitor.email.trim(),
        ...(visitor.phone.trim() ? { phone: visitor.phone.trim() } : {}),
        ...(waitlist ? { waitlist: true } : {}),
        agreedToTerms: true,
      });
      /* Already-registered (JAI-2279, absorbed 2026-10-05): a duplicate carries no reference; report it as its own outcome, never as "registered". */
      if (result.alreadyRegistered) return { reference: "", handle: null, outcome: "already" };
      const registration = result.registration as Record<string, unknown> | undefined;
      // TWO identifiers, and they are not interchangeable: `orderRef` is shown,
      // `registration.id` is held. Cancelling with the display reference fails,
      // and it fails for the first time against a real tenant (kit §2.3b).
      return {
        reference: result.orderRef || result.confirmationNumber,
        handle: stringOr(registration?.["id"], null),
        outcome: stringOr(registration?.["status"], null) === "waitlist" ? "waitlist" : "registered",
      };
    },

    async lookupVisits() {
      // `getEventRegistration` / `cancelEventRegistration` and the guest lookup
      // have no contract operation and no mock; they 404 against MSW. Refusing
      // here is honest — calling a route that does not exist is not.
      throw new OneStopActionsUnavailable("");
    },
  };
}

/**
 * Nothing can be called, and every control says so. The default with no
 * provider, and the right implementation for the Journey Builder preview.
 */
export function unavailableActions(reason: string): OneStopActions {
  const refuse = (): never => {
    throw new OneStopActionsUnavailable(reason);
  };
  return {
    available: false,
    unavailableReason: reason,
    simulated: false,
    joinQueue: async () => refuse(),
    queuePosition: async () => refuse(),
    leaveQueue: async () => refuse(),
    holdOpening: async () => refuse(),
    holdSlot: async () => refuse(),
    openingsOn: async () => refuse(),
    releaseHold: async () => refuse(),
    checkoutHold: async () => refuse(),
    registerForEvent: async () => refuse(),
    lookupVisits: async () => refuse(),
  };
}

/* ── the simulated implementation ────────────────────────────────────────── */

/**
 * SIMULATED, NOT LIVE, NOT DISABLED — the third thing, and it exists because the
 * first two left an operator with no way to walk a journey they had just built.
 *
 * `livePortalActions()` needs a customer edge, a real queue and a real hold, and
 * an admin in a builder must not fire any of them on a visitor's behalf.
 * `unavailableActions()` renders every control greyed with a reason, which
 * answers "is the button there" and nothing else — an operator asking "what does
 * a student SEE after they tap Join" got a tooltip. This implementation answers
 * that question: the whole flow runs, in memory, in the operator's own tab.
 *
 * WHAT IS FAITHFUL, AND WHY THAT MATTERS MORE THAN THE FAKE DATA
 * The blocks are unchanged. Every state they own is reached the same way it is
 * reached live — the same interface, the same promise timing, the same required
 * fields, the same `useCta` running/done/error machine, the same progressive
 * disclosure, the same idempotency behaviour on a double tap (a second join with
 * the same phone RECOVERS the first entry and says so, because that is what the
 * live join does). A simulation that skipped the details form, or accepted an
 * empty name, would be a walkthrough of a screen nobody will ever ship.
 *
 * WHAT IS INVENTED, AND SAID SO
 * Ticket numbers, check-in codes, hold ids, booking references, registration
 * references, positions, waits and the openings behind them. All of it is
 * deterministic per id — the same desk always seeds the same queue depth, so a
 * screenshot taken twice matches — and none of it is written anywhere: no
 * network call, no storage, no document. Closing the tab is the reset. The HOST
 * is responsible for labelling the frame; `simulated` is how it knows to.
 *
 * REAL LATENCY, ON PURPOSE. Every method waits ~`LATENCY_MS` before resolving.
 * The loading state IS part of the flow being reviewed — an instant answer hides
 * the "Working…" label, the disabled-while-in-flight rule, and the fact that a
 * student on a phone waits at all.
 *
 * THE TOKEN RULE STILL HOLDS. The entry token is a key into this closure's own
 * Map and is handed back to the block, which keeps it in component state exactly
 * as it keeps a live one. Not a URL, not storage, not a module singleton — the
 * state lives in the instance the host memoized, so it cannot leak across tabs.
 */

const LATENCY_MS = 280;

const pause = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Stable small integer from an id — the seed behind every invented figure. */
function hashOf(text: string): number {
  let h = 0;
  for (const ch of text) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h;
}

const hhmm = (minutes: number): string =>
  `${String(Math.floor(minutes / 60) % 24).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

/**
 * The civil calendar day in a zone, `dayOffset` days from now. `en-CA` is used
 * for one reason: it formats as `YYYY-MM-DD`, which is the contract's own civil
 * date shape, so no re-assembly happens here.
 */
function civilDayIn(timeZone: string, dayOffset: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(Date.now() + dayOffset * 86_400_000));
}

/** Business hours the invented openings fall inside. */
const DAY_OPEN_MIN = 9 * 60;
const DAY_CLOSE_MIN = 17 * 60;

/**
 * A stand-in opening for a service the preview has no availability for.
 *
 * This is the ONE thing the blocks branch on `simulated` for, and it is a data
 * gap rather than a behaviour change: a live appointments fragment carries
 * `nextOpenings`, the builder's preview fragment carries `[]` on purpose
 * (availability is a live query it cannot run), and a row with no opening has
 * nothing to hold — so it renders "No openings in this window" and the flow ends
 * before it starts. With a simulated opening the row books exactly as it books
 * live. TOMORROW, never today: a time already past would be a different kind of
 * wrong on screen.
 */
export function simulatedOpening(
  serviceId: string,
  durationMinutes: number | null,
  timeZone: string,
): PortalOpening {
  const duration = durationMinutes && durationMinutes > 0 ? durationMinutes : 30;
  // SALTED WITH THE DURATION, because the service id alone put two services on
  // the same minute often enough to look like a bug in the page rather than a
  // coincidence in the seed. 16 half-hour slots span 09:00–16:30.
  const slot = hashOf(`${serviceId}:${duration}`) % 16;
  return {
    start: {
      date: civilDayIn(timeZone, 1),
      time: hhmm(DAY_OPEN_MIN + slot * 30),
      timeZone,
    },
    durationMinutes: duration,
    staffId: null,
    /* `staffName: null` was here and `PortalOpening` is `.strict()` — it has no
       such field, and the `as PortalOpening` cast was the only reason a strict
       schema mismatch compiled (2026-09-08). `locationId` is the field that
       belongs, and NULL is the only legal value: the contract states nothing may
       populate it until per-opening placement exists, "MOCKS INCLUDED". Cast
       dropped so the object is checked rather than asserted. */
    locationId: null,
  };
}

/**
 * THE ONE SEED BEHIND A DESK'S DEPTH, exported because two surfaces have to
 * agree on it.
 *
 * The queue card in the OneStop tab prints a preview wait and a people-ahead
 * figure; joining that same desk has to land BEHIND exactly those people (the
 * kit's own rule: "the join lands at position N+1 where the card said N
 * ahead"). While the tab seeded the card and this file seeded the join, the two
 * disagreed on screen — the card said 4 ahead and the ticket said 6 — in the
 * one review this feature exists for. Both now read here.
 */
export function simulatedQueueDepth(queueId: string): {
  readonly waitMinutes: number;
  readonly peopleAhead: number;
} {
  const waitMinutes = 4 + (hashOf(queueId) % 22); // 4–25 min
  return { waitMinutes, peopleAhead: Math.max(0, Math.round(waitMinutes / 4) - 1) };
}

interface SimEntry {
  queueId: string;
  ticket: string;
  checkInCode: string;
  peopleAhead: number;
  estimatedWaitMinutes: number;
  /** The desk's per-person estimate, derived from the seed the CARD showed. */
  perPerson: number;
  state: QueueEntryState;
  /** What made the join idempotent, when it was given. */
  phone: string;
}

interface SimHold {
  holdId: string;
  serviceId: string;
  startUtc: string;
  endUtc: string;
  expiresAt: string;
  spent: boolean;
}

/** How long a simulated hold lives. The live default, so the countdown matches.
 *
 *  Do NOT shorten this to make `HOLD_EXPIRED` easier to reach: `checkout`
 *  requires three typed fields, so anything under ~a minute expires the hold
 *  mid-form and the CONFIRMED path becomes the unreachable one instead. The
 *  refusal is reachable as it is live — leave the held card sitting. */
const SIM_HOLD_MS = 5 * 60_000;

export function simulatedPortalActions(options?: {
  /** The document's zone — invented openings are civil times IN it. */
  readonly timeZone?: string;
  /** Fallback service length when neither the fragment nor a hold said. */
  readonly defaultDurationMinutes?: number;
}): OneStopActions {
  const timeZone = options?.timeZone || "UTC";
  const defaultDuration = options?.defaultDurationMinutes ?? 30;

  const entries = new Map<string, SimEntry>();
  const holds = new Map<string, SimHold>();
  /** Learned from the openings a row hands back, so a searched slot keeps its length. */
  const durations = new Map<string, number>();
  let seq = 0;
  const nextId = (prefix: string): string => {
    seq += 1;
    return `${prefix}_sim_${seq}`;
  };

  const slotsFor = (serviceId: string, date: string): readonly AvailabilitySlot[] => {
    const duration = durations.get(serviceId) ?? defaultDuration;
    const step = duration + 15;
    const first = DAY_OPEN_MIN + (hashOf(`${serviceId}${date}`) % 4) * 15;
    const out: AvailabilitySlot[] = [];
    for (let i = 0; i < 8; i += 1) {
      const startMin = first + i * step;
      if (startMin + duration > DAY_CLOSE_MIN) break;
      const startUtc = zonedCivilToInstant({ date, time: hhmm(startMin), timeZone } as PortalZonedTime);
      out.push({
        startUtc,
        endUtc: new Date(Date.parse(startUtc) + duration * 60_000).toISOString(),
        staffId: null,
        staffName: null,
        resourceId: null,
        durationMinutes: duration,
      });
    }
    return out;
  };

  const hold = (serviceId: string, startUtc: string, endUtc: string): AppointmentHold => {
    const record: SimHold = {
      holdId: nextId("hold"),
      serviceId,
      startUtc,
      endUtc,
      expiresAt: new Date(Date.now() + SIM_HOLD_MS).toISOString(),
      spent: false,
    };
    holds.set(record.holdId, record);
    return {
      holdId: record.holdId,
      expiresAt: record.expiresAt,
      startUtc: record.startUtc,
      endUtc: record.endUtc,
    };
  };

  /** The same envelope shape `describeError` reads, so refusals render normally. */
  const refuse = (code: string, message: string): never => {
    throw Object.assign(new Error(message), { code, source: "envelope", fields: [], details: {} });
  };

  return {
    available: true,
    unavailableReason: null,
    simulated: true,

    async joinQueue({ queueId, visitor }) {
      await pause(LATENCY_MS);
      const phone = visitor.phone.trim();
      // IDEMPOTENCY, SIMULATED THE WAY IT REALLY WORKS: a phone is what makes a
      // repeat join the SAME entry, and the block's "you're already in this
      // line" state is only reachable if this branch exists.
      if (phone) {
        for (const [token, entry] of entries) {
          if (entry.queueId === queueId && entry.phone === phone && !TERMINAL_SIM.has(entry.state)) {
            return {
              reference: entry.ticket,
              checkInCode: entry.checkInCode,
              positionToken: token,
              peopleAhead: entry.peopleAhead,
              estimatedWaitMinutes: entry.estimatedWaitMinutes,
              state: entry.state,
              recovered: true,
            };
          }
        }
      }
      const seed = hashOf(queueId);
      // The depth the CARD printed — same function, so the ticket lands behind
      // exactly the people the desk said were in front of it.
      const depth = simulatedQueueDepth(queueId);
      const entry: SimEntry = {
        queueId,
        ticket: `${String.fromCharCode(65 + (seed % 26))}-${String(100 + (seed % 800) + entries.size)}`,
        checkInCode: String(1000 + ((seed * 7 + entries.size * 13) % 9000)),
        peopleAhead: depth.peopleAhead,
        estimatedWaitMinutes: depth.waitMinutes,
        perPerson: Math.max(1, Math.round(depth.waitMinutes / (depth.peopleAhead + 1))),
        state: "waiting",
        phone,
      };
      const token = nextId("entry");
      entries.set(token, entry);
      return {
        reference: entry.ticket,
        checkInCode: entry.checkInCode,
        positionToken: token,
        peopleAhead: entry.peopleAhead,
        estimatedWaitMinutes: entry.estimatedWaitMinutes,
        state: entry.state,
        recovered: false,
      };
    },

    async queuePosition({ positionToken }) {
      await pause(LATENCY_MS);
      const entry = entries.get(positionToken);
      if (!entry) refuse("NOT_FOUND", "No such entry");
      const found = entry as SimEntry;
      // THE LINE MOVES. Each poll advances it one place, then through the public
      // status vocabulary — `called` is what the desk card's "You're up" state
      // is waiting for, and without this the panel would sit at its first frame
      // for as long as the operator watched it.
      if (found.peopleAhead > 0) {
        found.peopleAhead -= 1;
        found.estimatedWaitMinutes = found.peopleAhead * found.perPerson;
      } else if (found.state === "waiting") {
        found.state = "called";
        found.estimatedWaitMinutes = 0;
      } else if (found.state === "called") {
        found.state = "serving";
      } else if (found.state === "serving") {
        found.state = "done";
      }
      return {
        reference: found.ticket,
        peopleAhead: found.peopleAhead,
        estimatedWaitMinutes: found.estimatedWaitMinutes,
        state: found.state,
      };
    },

    async leaveQueue({ positionToken }) {
      await pause(LATENCY_MS);
      const entry = entries.get(positionToken);
      if (entry) entry.state = "left";
      entries.delete(positionToken);
    },

    async holdOpening({ serviceId, opening }) {
      await pause(LATENCY_MS);
      durations.set(serviceId, opening.durationMinutes);
      const startUtc = zonedCivilToInstant(opening.start);
      return hold(
        serviceId,
        startUtc,
        new Date(Date.parse(startUtc) + opening.durationMinutes * 60_000).toISOString(),
      );
    },

    async holdSlot({ serviceId, slot }) {
      await pause(LATENCY_MS);
      durations.set(serviceId, slot.durationMinutes);
      return hold(serviceId, slot.startUtc, slot.endUtc);
    },

    async openingsOn({ serviceId, date }) {
      await pause(LATENCY_MS);
      return slotsFor(serviceId, date);
    },

    async releaseHold({ holdId }) {
      await pause(LATENCY_MS);
      holds.delete(holdId);
    },

    async checkoutHold({ holdId, visitor }) {
      await pause(LATENCY_MS);
      const record = holds.get(holdId);
      // The two refusals this flow really has, with the wire's own codes, so the
      // block's expired / already-booked copy is reachable in a walkthrough
      // instead of only in production.
      if (!record || record.spent) refuse("HOLD_INVALID", "That hold is no longer valid");
      const live = record as SimHold;
      if (Date.parse(live.expiresAt) < Date.now()) {
        holds.delete(holdId);
        refuse("HOLD_EXPIRED", "That hold has expired");
      }
      live.spent = true;
      holds.delete(holdId);
      const seed = hashOf(`${live.serviceId}${visitor.email}${live.startUtc}`);
      return {
        // `manageUrl` is null and stays null: a manage link is a signed bearer
        // URL only a real checkout can mint, and inventing one would put a dead
        // credential-shaped string on screen.
        reference: `SIM-${String(seed % 1_000_000).padStart(6, "0")}`,
        manageUrl: null,
      };
    },

    async registerForEvent({ eventId, visitor, waitlist }) {
      await pause(LATENCY_MS);
      const seed = hashOf(`${eventId}${visitor.email}`);
      return {
        reference: `SIM-EV-${String(seed % 100_000).padStart(5, "0")}`,
        handle: `reg_sim_${seed % 100_000}`,
        // The branch is the server's live: the block asks for the waitlist when
        // the resolved state said full, and the answer echoes what it got.
        outcome: waitlist ? "waitlist" : "registered",
      };
    },

    async lookupVisits() {
      await pause(LATENCY_MS);
      // No operation exists for this even live (the guest lookup is unbuilt), so
      // the simulation refuses too, with the same class of failure. Pretending
      // to find visits would be the one place this implementation lied about
      // what the platform can do.
      throw new OneStopActionsUnavailable("");
    },
  };
}

/** Terminal states for the recovery check. Mirrors the blocks' own `TERMINAL`. */
const TERMINAL_SIM: ReadonlySet<QueueEntryState> = new Set<QueueEntryState>([
  "done",
  "left",
  "no-show",
]);

/* ── handoff ─────────────────────────────────────────────────────────────── */

/** What the visitor tapped: one of the three journeys, plus the id it needs. */
/**
 * The three kinds gain an
 * optional `url`. Gold Master composes a destination from a slug; the slug is
 * tenant data and the destination is on a DIFFERENT host, so only the server
 * can resolve it. Returns with CD's own reader of `joinUrl`/`bookUrl`/
 * `registerUrl`, at which point this patch is dropped verbatim.
 *
 * A hand-off target. `url` is the server-resolved ABSOLUTE destination and is
 * preferred over every composed form; it is optional only because the kinds do
 * not all carry one yet.
 */
export type OneStopHandoffTarget =
  | {
      readonly kind: "appointment";
      readonly url?: string | null;
      /** The service the row was for — the flow opens on it. */
      readonly serviceId: string;
      readonly label?: string | null;
    }
  | {
      readonly kind: "queue";
      /** The server-resolved join page for THIS desk. */
      readonly url?: string | null;
      /** The desk the card was for. */
      readonly queueId: string;
      /**
       * The service that desk serves, WHEN THE DOCUMENT CARRIES ONE. A queue
       * item has no `serviceId` in the portal contract today (kit §2.2e), so
       * this is usually null and the queue id is what the walk-in journey
       * resolves its service from. Passed through, never guessed.
       */
      readonly serviceId?: string | null;
      readonly label?: string | null;
    }
  | {
      readonly kind: "event";
      /** The server-resolved registration page for THIS event. */
      readonly url?: string | null;
      /** The event being registered for. */
      readonly eventId: string;
      readonly label?: string | null;
    };

/**
 * THE RETURN-STRIP QUERY CONTRACT, IN ONE PLACE.
 *
 * WHICH journey, service, desk or event a hand-off opens is the SERVER'S
 * answer, carried inside the resolved URL itself (`bookUrl`, `joinUrl`,
 * `registerUrl`, or a grant's `destination`). The only parameters this module
 * adds are the ones that let the journey page draw its "Back to One Stop" strip.
 *
 * `from=onestop` travels with all three so a flow can offer a way back to the
 * front door, and so the analytics on those pages can tell a front-door arrival
 * from a direct link.
 */
export const HANDOFF_PARAMS = {
  source: "from",
  /** The One Stop page's own URL — where "Back to One Stop" goes when the tab cannot close itself. */
  returnUrl: "return",
  /** The One Stop page's name, for the strip's label ("Back to Student Center One-Stop"). */
  fromLabel: "fromLabel",
} as const;

/** Where the three journeys open from a One Stop page — the page's own setting. */
export type OneStopJourneyTarget = "tab" | "same";

/**
 * The parameters that let a journey page show its "Back to One Stop" strip and
 * find its way back. Appended to EVERY hand-off URL — the server-resolved one
 * included — because the strip is keyed on `from=onestop` and nothing else.
 */
export function withReturnParams(
  url: string,
  options?: { readonly returnUrl?: string | null; readonly fromLabel?: string | null },
): string {
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.href : "https://localhost/");
    u.searchParams.set(HANDOFF_PARAMS.source, "onestop");
    if (options?.returnUrl) u.searchParams.set(HANDOFF_PARAMS.returnUrl, options.returnUrl);
    if (options?.fromLabel) u.searchParams.set(HANDOFF_PARAMS.fromLabel, options.fromLabel.slice(0, 60));
    // A relative input stays relative on the way out.
    return /^https?:/i.test(url) ? u.toString() : `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return url;
  }
}

/**
 * `https:`, absolute, and no embedded credentials — the same test
 * `screens/portal/appointment-destination.ts` applies before a server-resolved
 * URL becomes a form action. It is duplicated here rather than imported because
 * this module is the blocks' own leaf and does not otherwise reach into
 * `screens/`; both copies are four lines and neither has a branch.
 *
 * EXPORTED so the block renderers gate on the SAME question this function
 * answers. They used to test `item.joinUrl` for truthiness, which is a
 * different question: a truthy-but-unnavigable value passed their guard,
 * suppressed the working inline flow, then failed the check here and left the
 * visitor with no destination. Asking two different questions about one value
 * is how a guard stops guarding.
 */
export function isBrowserNavigable(raw: string): boolean {
  try {
    const url = new URL(raw);
    return url.protocol === "https:" && url.username === "" && url.password === "";
  } catch {
    return false;
  }
}

/**
 * The SERVER-RESOLVED journey URL for one tap, with the return-strip params —
 * or `null` when the server resolved none.
 *
 * THE SERVER'S ANSWER IS THE ONLY ONE THAT CAN BE RIGHT. The destination lives
 * on a DIFFERENT host from the portal, and that host is not derivable in the
 * browser — so the page response carries it, resolved from the tenant's own
 * public-hostname configuration (`PortalAppointmentItem.bookUrl`,
 * `PortalQueueItem.joinUrl`, `PortalEventItem.registerUrl`). There is no
 * composed fallback: a relative `/book/{slug}` opened the portal's own SPA
 * shell, and an origin-prefixed one guessed a journey slug the tenant may never
 * have published. `null` is the caller's cue to say "unavailable".
 *
 * ONLY an `https:` URL, and never one carrying embedded credentials. This
 * value arrives over the wire and is handed to `window.open` — and, when a
 * popup is refused, to `window.location.assign`, which navigates the PORTAL'S
 * OWN top frame. The contract types the field `z.string().url()`, and that
 * admits `javascript:` and `data:` as readily as `https:`, so the type is not
 * the guard.
 */
export function handoffUrl(
  target: OneStopHandoffTarget,
  ret?: { readonly returnUrl?: string | null; readonly fromLabel?: string | null },
): string | null {
  if (target.url && isBrowserNavigable(target.url)) return withReturnParams(target.url, ret);
  return null;
}

/**
 * A `handoff` that opens the journey in a NEW TAB.
 *
 * NEW TAB, NOT A NAVIGATION, and that is the operator's call (2026-09-04): the
 * front door is a board people come back to. Someone who books an advising slot
 * still wants the wait times and the events behind it, and on a lobby kiosk or
 * inside an embedded iframe, replacing the page strands them somewhere they
 * cannot get back from. The opened page must never keep a handle onto this
 * document: the opener is severed on the returned handle, which is not the same
 * as asking `window.open` for it as a feature — see below.
 *
 * ⚠️ APPOINTMENTS NO LONGER COME THROUGH HERE ON THE PORTAL HOST (2026-09-09).
 * `PortalHome` owns that target now: signed in it POSTs a one-time grant to the
 * destination the server resolved, and anonymously it uses the published
 * journey's own `publicUrl`. Both are absolute URLs on the booking host, and
 * neither may carry `noreferrer` (it nulls the `Origin` the grant redemption
 * requires) or navigate the OneStop tab. This helper serves anonymous QUEUE and
 * EVENT targets, and only ever with the server-resolved `joinUrl` /
 * `registerUrl`: a target without one opens nothing here, and the host shows
 * its unavailable state instead (`PortalHome` checks before delegating).
 *
 * A BLOCKED POPUP IS NOT A DEAD BUTTON. `window.open` returns null when the
 * browser refuses, and the fallback navigates the top frame instead. A refused
 * popup that silently did nothing would be the worst of the three outcomes, and
 * it is the one a kiosk browser is most likely to produce.
 *
 * ⛔ `noopener` MUST NOT GO IN THE FEATURES STRING, and this is the trap the
 * fallback above walks into. The HTML spec makes `window.open` return null
 * whenever the features carry `noopener` — even when the tab opened fine — so
 * "the popup was refused" reads true on EVERY click and the board navigates
 * away alongside the new tab it just opened. Measured on a live tenant: one
 * click on Join the line, two copies of the join page. The opener is severed on
 * the returned handle instead, which is the same isolation without the null.
 * `noreferrer` goes with it: these are GET hand-offs to the tenant's own public
 * hosts, and the destination is served with a referrer policy that sends the
 * board's origin only.
 */
export function openInNewTab(
  options?: {
    /**
     * READ AT CLICK TIME, not at install time: the page document (and so the
     * setting) arrives after this helper is built. `"same"` navigates this tab;
     * the default `"tab"` opens a new one. KP, 2026-09-17: the One Stop page's
     * SETUP › General "Where journeys open" setting.
     */
    readonly mode?: () => OneStopJourneyTarget;
    /**
     * WHERE `"same"` ACTUALLY GOES (docket 2026-09-18 item 1). "This tab" means
     * the journey opens in a popup ON the One Stop page, not that the board
     * navigates away: the screen renders the dialog and this returns TRUE when
     * it took the journey. FALSE — no popup mounted, or it declined — falls
     * through to the navigation this mode did before, because a hand-off that
     * silently does nothing is the worst of the outcomes.
     */
    readonly frame?: (url: string, target: OneStopHandoffTarget) => boolean;
    /** The One Stop page's own URL and name, for the journey's return strip. */
    readonly returnUrl?: () => string | null;
    readonly fromLabel?: () => string | null;
  },
): (target: OneStopHandoffTarget) => void {
  return (target) => {
    const ret = { returnUrl: options?.returnUrl?.() ?? null, fromLabel: options?.fromLabel?.() ?? null };
    const url = handoffUrl(target, ret);
    // No server-resolved destination: never a composed guess. The host is
    // expected to have shown its unavailable state before delegating here.
    if (!url) {
      console.warn("[one-stop] hand-off has no server-resolved destination:", target.kind);
      return;
    }
    if ((options?.mode?.() ?? "tab") === "same") {
      // The popup first: the board stays put and the journey runs over it.
      if (options?.frame?.(url, target)) return;
      // No popup available — the operator chose to leave the board, and the
      // journey's strip brings them back.
      window.location.assign(url);
      return;
    }
    const opened = window.open(url, "_blank");
    if (opened) {
      // The new tab must not keep a handle back into the board. Set on the
      // handle rather than requested as a feature, because the feature form
      // is exactly what returns null and re-creates the double open.
      opened.opener = null;
      return;
    }
    window.location.assign(url);
  };
}

/* ── the provider ────────────────────────────────────────────────────── */

const ActionsContext = createContext<OneStopActions | null>(null);

export function OneStopActionsProvider(props: {
  actions: OneStopActions;
  children: ReactNode;
}): JSX.Element {
  return <ActionsContext.Provider value={props.actions}>{props.children}</ActionsContext.Provider>;
}

/**
 * The actions in force. A renderer mounted without a provider gets an
 * unavailable implementation rather than a crash — exactly right for the Journey
 * Builder preview, where a CTA must render but must never join a real queue or
 * take a real booking on an admin's behalf.
 */
export function useOneStopActions(): OneStopActions {
  return useContext(ActionsContext) ?? FALLBACK;
}

const FALLBACK = unavailableActions("");

/* ── the per-CTA state machine ───────────────────────────────────────────── */

/**
 * The WIRE code, verbatim, for the branches that are normal outcomes rather than
 * failures. `null` when the failure carried no code we can branch on.
 */
export type CtaErrorCode =
  | "SLOT_UNAVAILABLE"
  | "HOLD_EXPIRED"
  | "HOLD_INVALID"
  | "QUEUE_NOT_OPEN"
  /** CC 0343, 0356: a closed or full queue; `queueRefusal` carries the sentence. */
  | "QUEUE_HOURS_UNAVAILABLE"
  | "QUEUE_CAPACITY_LIMIT_EXCEEDED"
  | "VALIDATION_ERROR"
  | "NOT_FOUND"
  /** JAI-2020 — the offering has an audience this visitor does not match
   *  (or is signed out). `audience` below carries the params. */
  | "AUDIENCE_FORBIDDEN"
  | null;

/** 403 AUDIENCE_FORBIDDEN params. The server never names the attribute or the
 *  values, on purpose, and nothing here asks for them. */
export interface AudienceRefusal {
  readonly subjectKind: "service" | "event" | "queue";
  readonly reason: string;
}

export type CtaState<T> =
  | { readonly status: "idle" }
  | { readonly status: "running" }
  | { readonly status: "done"; readonly value: T }
  | {
      readonly status: "error";
      readonly message: string;
      readonly code: CtaErrorCode;
      /** `network` = we could not reach the server; `envelope` = it refused. */
      readonly source: string | null;
      /**
       * What the refusal named, normalised. A message that named a FIELD is
       * answered at that field, so the generic line stands down instead of
       * repeating something vaguer underneath it.
       */
      readonly refusal: RefusalMessages;
      /** Set only for AUDIENCE_FORBIDDEN. */
      readonly audience?: AudienceRefusal | null;
      /** Set only for QUEUE_HOURS_UNAVAILABLE / QUEUE_CAPACITY_LIMIT_EXCEEDED: the visitor's sentence, from code + params. */
      readonly queueRefusal?: string | null;
    };

export interface Cta<T, A extends unknown[]> {
  readonly state: CtaState<T>;
  readonly run: (...args: A) => void;
  readonly reset: () => void;
}

/**
 * Loading / success / error for one control. Every CTA in this package has all
 * three, because "the form renders" is not a round trip: a student needs to know
 * the tap landed, what came back, and what to do when it did not.
 *
 * `run` is a no-op while the call is in flight. That is the disable rule
 * expressed in the state machine as well as on the button, because `checkout` is
 * not double-tap-safe and a disabled attribute is one keyboard path away from
 * being bypassed.
 *
 * `onError` receives the RAW failure, not the described one: the form binds the
 * refusal's field map onto its inputs, and that needs the error itself.
 */
export function useCta<T, A extends unknown[]>(
  operation: (...args: A) => Promise<T>,
  onDone?: (value: T) => void,
  onError?: (err: unknown) => void,
): Cta<T, A> {
  const [state, setState] = useState<CtaState<T>>({ status: "idle" });
  const inFlight = state.status === "running";

  const run = useCallback(
    (...args: A) => {
      if (inFlight) return;
      setState({ status: "running" });
      operation(...args).then(
        (value) => {
          setState({ status: "done", value });
          onDone?.(value);
        },
        (err: unknown) => {
          setState({ status: "error", ...describeError(err) });
          onError?.(err);
        },
      );
    },
    [operation, onDone, onError, inFlight],
  );

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return useMemo(() => ({ state, run, reset }), [state, run, reset]);
}

/**
 * The failure, as much of it as the wire actually carried.
 *
 * `isApiError`, never `instanceof` — the client is bundled into several contexts
 * and `instanceof` answers `false` across a duplicated module instance. The
 * message is the envelope's OWN copy when it has one and nothing user-facing
 * when it does not: we never invent an explanation for a failure we did not
 * diagnose. Branching is on `code`, never on `message`.
 *
 * ⛔ `message` IS FOR DIAGNOSIS, NOT FOR A CUSTOMER. It is a developer-facing
 * English string and untranslated by construction — the client says so itself —
 * so nothing in this package renders it. `"Invalid request body"` reached a
 * student's screen exactly once, and that is what `refusal` is now carried for.
 */
export function describeError(err: unknown): {
  message: string;
  code: CtaErrorCode;
  source: string | null;
  refusal: RefusalMessages;
  audience?: AudienceRefusal | null;
  queueRefusal?: string | null;
} {
  if (isApiError(err)) {
    return {
      message: typeof err.message === "string" ? err.message : "",
      code: KNOWN_CODES.has(err.code) ? (err.code as CtaErrorCode) : null,
      source: typeof err.source === "string" ? err.source : null,
      refusal: refusalMessages(err),
      audience: err.code === "AUDIENCE_FORBIDDEN" ? audienceRefusalOf(err.params) : null,
      queueRefusal: queueRefusalText(err),
    };
  }
  if (err && typeof err === "object" && "message" in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message) {
      return { message, code: null, source: null, refusal: NO_MESSAGES };
    }
  }
  return { message: "", code: null, source: null, refusal: NO_MESSAGES };
}

/** Unknown subjectKind reads as "service" only for copy; reason passes through. */
function audienceRefusalOf(params: unknown): AudienceRefusal {
  const p = (params && typeof params === "object" ? params : {}) as Record<string, unknown>;
  const kind = p.subjectKind === "event" || p.subjectKind === "queue" ? p.subjectKind : "service";
  return { subjectKind: kind, reason: typeof p.reason === "string" ? p.reason : "" };
}

const KNOWN_CODES: ReadonlySet<string> = new Set([
  "AUDIENCE_FORBIDDEN",
  "SLOT_UNAVAILABLE",
  "HOLD_EXPIRED",
  "HOLD_INVALID",
  "QUEUE_NOT_OPEN",
  "VALIDATION_ERROR",
  "NOT_FOUND",
  "QUEUE_HOURS_UNAVAILABLE",
  "QUEUE_CAPACITY_LIMIT_EXCEEDED",
]);
